import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { push } from 'react-router-redux';
import MomentUtils from '@date-io/moment';
import queryString from 'query-string';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  Link, Paper, Divider, Card, CardContent, CardHeader,
  IconButton, Grid, Typography, Avatar, Collapse,
  Chip, SnackbarContent, Snackbar
} from '@material-ui/core';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core/styles';
import {
  Close as CloseIcon, Done as DoneIcon, KeyboardArrowRight,
  KeyboardArrowLeft, CalendarToday, DateRange,
  Timer, PinDrop, Info as InfoIcon
} from '@material-ui/icons';
import BookingForm from './BookingForm';
import MultipleGroupBookingView from './MultipleGroupBookingView';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import routes from '../../library/Routes';
import moment from 'moment';
import { RouteComponentProps } from 'react-router-dom';

interface StyleProps {
  typography: string;
  card: string;
  media: string;
  cover: string;
  closeButton: string;
  root: string;
  schedule: string;
  rooms: string;
  formControl: string;
  selectEmpty: string;
  coursesGrid: string;
  expand: string;
  expandOpen: string;
  buttons: string;
  details: string;
  studioCard: string;
  studioCover: string;
  bigAvatar: string;
  timeSelect: string;
  alternativeCourses: string;
}

const styles = (theme: Theme) => createStyles({
  typography: {
    padding: theme.spacing(2),
  },
  card: {
    display: 'flex',
  },
  media: {
    height: 0,
    paddingTop: '56.25%', // 16:9
  },
  cover: {
    width: 150,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  root: {
    maxWidth: '400px',
    margin: 'auto',
    padding: theme.spacing(1),
  },
  schedule: {
    maxWidth: theme.spacing(170),
    margin: `0 auto`,
    padding: `0 ${theme.spacing(2)}px`,
  },
  rooms: {
    margin: theme.spacing(1),
    padding: `${theme.spacing(2)}px 0`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: theme.spacing(15),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  coursesGrid: {
    paddingTop: theme.spacing(2),
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  buttons: {
    '& > *': {
      flex: 1,
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
  studioCard: {},
  studioCover: {
    height: 0,
    paddingTop: '56.25%', // 16:9
  },
  bigAvatar: {
    width: 150,
    height: 150,
  },
  timeSelect: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  alternativeCourses: {
    marginTop: theme.spacing(2),
  },
});

interface RouteParams {
  id?: string;
  domainName?: string;
  courseId: string;
}

interface Studio {
  _id: string;
  id: string;
  owner_id: string;
  name: string;
  avatar?: string;
  roomsMap?: Record<string, Room>;
  pricing?: string;
  bookFrom: number;
  bookTo: number;
  domainName?: string;
  [key: string]: any;
}

interface Room {
  name: string;
  [key: string]: any;
}

interface Owner {
  _id: string;
  timezone: string;
  [key: string]: any;
}

interface Course {
  _id: string;
  title: string;
  avatar?: string;
  description?: string;
  billingTag: string;
  room: string[];
  private: boolean;
  allowBookEmptySlots: boolean;
  allowMultiBooking: boolean;
  beforeBookingText?: string;
  [key: string]: any;
}

interface FreeSlot {
  classDateTime: string;
  teacherId: string[] | string;
  groupId: string;
  [key: string]: any;
}

interface Teacher {
  _id: string;
  name: string;
  avatar?: string;
  [key: string]: any;
}

interface AlternativeCourseData {
  course: Course;
  teacher?: Teacher;
  classDates: FreeSlot[];
}

interface GroupBookingCourseViewProps extends WithStyles<typeof styles>, RouteComponentProps<RouteParams> {
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  studio: any;
  domain: {
    id: string;
    [key: string]: any;
  };
  date: moment.Moment;
  registerToClass: (studio: Studio, classId: string) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new?: boolean }) => void;
}

interface GroupBookingCourseViewState {
  studio?: Studio;
  owner?: Owner;
  notifications: any[];
  showFullDescription: boolean;
  showFullPricing: boolean;
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  repeatableClass?: any;
  categoryLinks: any[];
  reviews: any[];
  coursesExpanded: Record<string, boolean>;
  courseEvents: Record<string, any>;
  to: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  date: moment.Moment;
  events?: FreeSlot[];
  selectedEvent?: FreeSlot;
  alternativeCourses?: AlternativeCourseData[];
  course?: Course;
  teacher?: Teacher;
  showAlternatives?: boolean;
  loadingAlternatives?: boolean;
  showPricingDetails?: boolean;
  showErrorSnackbar?: boolean;
}

class GroupBookingCourseView extends React.Component<GroupBookingCourseViewProps, GroupBookingCourseViewState> {
  constructor(props: GroupBookingCourseViewProps) {
    super(props);
    const pageState = (window as any).__PAGE_STATE__;
    this.state = {
      studio: pageState && pageState.studio,
      owner: pageState && pageState.studioOwner,
      notifications: [],
      showFullDescription: false,
      showFullPricing: false,
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      repeatableClass: undefined,
      categoryLinks: [],
      reviews: [],
      coursesExpanded: {},
      courseEvents: {},
      to: moment.utc().add(1, 'days').endOf('isoWeek'),
      minDate: moment.utc().add(1, 'days'),
      maxDate: moment.utc().add(1, 'weeks').add(1, 'days'),
      date: props.date,
    };
  }

  componentDidMount() {
    if (!this.state.studio) {
      let api = serviceLocator.studioApi;
      let domainName = this.props.match.params.domainName;
      if (domainName) {
        return api
          .getStudioByDomainName(domainName)
          .then(this.onSuccess, LayoutHelper.onErr);
      } else {
        let id = this.props.match.params.id;
        if (id) {
          return api.getStudioById(id).then(this.onSuccess, LayoutHelper.onErr);
        }
      }
    } else {
      this.initDates(this.state.studio, this.state.owner);
    }
  }

  loadEvents = (studio: Studio, courseId: string, date: moment.Moment, owner: Owner) => {
    this.setState({
      events: undefined,
      selectedEvent: undefined,
      alternativeCourses: undefined,
    });
    serviceLocator.registrationApi
      .getFreeSlotsOnCourse(studio._id, courseId, date, owner.timezone, true)
      .then((events: { classDates: FreeSlot[]; studio: Studio; course: Course; teacher: Teacher }) => {
        let freeSlots = events.classDates;

        let changeSet: Partial<GroupBookingCourseViewState> = { events: freeSlots };
        if (freeSlots && freeSlots.length <= 2) {
          changeSet.showAlternatives = true;
        } else {
          changeSet.showAlternatives = false;
        }
        if (!this.state.studio) {
          changeSet.studio = events.studio;
        }
        if (!this.state.course) {
          changeSet.course = events.course;
        } else {
          changeSet.course = this.state.course;
        }
        if (!this.state.teacher) {
          changeSet.teacher = events.teacher;
        }
        this.setState(changeSet as GroupBookingCourseViewState);
        if (changeSet.course) {
          this.loadAltrnatives(
            studio,
            changeSet.course,
            date,
            this.state.owner as Owner
          );
        }
      });
  };

  loadAltrnatives = (studio: Studio, course: Course, date: moment.Moment, owner: Owner) => {
    this.setState({ loadingAlternatives: true });
    serviceLocator.registrationApi
      .getAlternativeFreeSlots(
        studio._id,
        course.billingTag,
        course._id,
        date,
        owner.timezone,
        true,
      )
      .then((alternativeCourses: AlternativeCourseData[]) => {
        this.setState({
          alternativeCourses: alternativeCourses,
          loadingAlternatives: false,
        });
      }, LayoutHelper.onErr);
  };

  onSuccess = (studio: Studio) => {
    this.loadOwnerInfo(studio);
  };

  loadOwnerInfo = (studio: Studio) => {
    serviceLocator.userApi.getOwnerInfo(studio.owner_id).then((owner: Owner) => {
      this.setState({ owner: owner, studio: studio });
      this.initDates(studio, owner);
    }, LayoutHelper.onErr);
  };

  initDates = (studio: Studio, owner: Owner) => {
    let date = this.props.date;
    const utcOffset = moment.tz(owner.timezone).utcOffset() / 60;
    let minDate = moment
      .utc()
      .add(studio.bookFrom + utcOffset, 'hours')
      .startOf('day');
    let maxDate = moment
      .utc()
      .add(studio.bookTo + utcOffset, 'hours')
      .startOf('day');
    if (date.diff(minDate) < 0 || date.diff(maxDate) > 0) {
      date = minDate;
    }
    this.setState({ date: date, minDate: minDate, maxDate: maxDate });
    this.loadEvents(studio, this.props.match.params.courseId, date, owner);
  };

  render() {
    let { classes } = this.props;

    return (
      <main className={classes.root}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {this.renderRepeatableClass(this.state.course)}
          </Grid>
          <Grid item xs={12}>
            {this.renderDatesSelect()}
          </Grid>
        </Grid>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={this.state.showErrorSnackbar}
          onClose={() => {
            this.setState({ showErrorSnackbar: false });
          }}
        >
          <SnackbarContent
            message={this.props.translate('manageRegistration.bookingError')}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={() => {
                  this.setState({ showErrorSnackbar: false });
                }}
              >
                <CloseIcon />
              </IconButton>,
            ]}
            style={{
              backgroundColor: '#d32f2f',
            }}
          />
        </Snackbar>
      </main>
    );
  }

  renderDatesSelect = () => {
    let { classes } = this.props;
    if (!this.state.course || !this.state.studio) {
      return LayoutHelper.renderLoader();
    }

    if (this.state.course.allowMultiBooking) {
      return (
        <MultipleGroupBookingView
          minDate={this.state.minDate}
          maxDate={this.state.maxDate}
          alternativeCourses={this.state.alternativeCourses}
          events={this.state.events}
          date={this.state.date}
          studio={this.state.studio}
          course={this.state.course}
          owner={this.state.owner}
        />
      );
    } else {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {this.renderTimeSelect()}
            <Divider />
            {this.renderNoEvents()}
            {this.renderClasses(this.state.course)}
            {this.state.selectedEvent ? (
              <BookingForm
                studio={this.state.studio}
                courseId={this.state.course._id}
                domain={this.props.domain}
                buttonTitle={`${this.props.translate('studio.class.signUp')}
                ${moment
                  .utc(this.state.selectedEvent.classDateTime)
                  .format('DD MMM YYYY HH:mm')}`}
                onSubmit={async (values: any) => {
                  try {
                    if (!this.state.selectedEvent) return;
                    
                    const classInfo = {
                      ...this.state.selectedEvent,
                      teacherId: this.state.selectedEvent.teacherId.length > 1 ?
                        [this.state.selectedEvent.teacherId[0]] :
                        this.state.selectedEvent.teacherId,
                    };
                    const attendanceData =
                      await serviceLocator.registrationApi.createFreeSlotOnCourse(
                        this.state.studio?.owner_id,
                        this.state.studio?._id,
                        this.state.course?._id,
                        classInfo,
                        values
                      );
                    const attendance = attendanceData.billedAttendance;
                    const studioClient = attendanceData.studioClient;
                    if (this.state.studio) {
                      this.props.clientAttendance(
                        this.state.studio,
                        attendance.groupId,
                        attendance._id,
                        studioClient
                      );
                    }
                  } catch (e) {
                    this.setState({ showErrorSnackbar: true });
                    if (this.state.studio && this.state.course && this.state.owner) {
                      this.loadEvents(
                        this.state.studio,
                        this.state.course._id,
                        this.state.date,
                        this.state.owner
                      );
                    }
                  }
                }}
                owner={this.state.owner}
              />
            ) : (
              this.renderAlternativeCourses()
            )}
          </Grid>
          {this.state.selectedEvent && (
            <Grid item xs={12}>
              {this.renderAlternativeCourses()}
            </Grid>
          )}
        </Grid>
      );
    }
  };

  renderNoEvents = () => {
    if (this.state.events && !this.state.loadingAlternatives) {
      if (
        this.state.events.length === 0 &&
        this.state.alternativeCourses &&
        this.state.alternativeCourses.length === 0
      ) {
        return (
          <Typography align="center">
            {this.props.translate('manageRegistration.noClassesAvailable')}
          </Typography>
        );
      }
    }
    return null;
  };

  renderTimeSelect = () => {
    let { date, minDate, maxDate } = this.state;
    let course = this.state.course;
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return (
          <Fragment>
            <Paper className={this.props.classes.timeSelect}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
                    <DatePicker
                      leftArrowIcon={<KeyboardArrowLeft />}
                      rightArrowIcon={<KeyboardArrowRight />}
                      keyboardIcon={<CalendarToday />}
                      dateRangeIcon={<DateRange />}
                      timeIcon={<Timer />}
                      minDate={this.state.minDate}
                      maxDate={this.state.maxDate}
                      format="DD MMM YYYY"
                      autoOk
                      inputVariant="outlined"
                      label={this.props.translate(
                        'manageRegistration.classDate'
                      )}
                      variant="inline"
                      value={this.state.date}
                      onChange={(date) => {
                        if (date && this.state.studio && this.state.owner && this.state.course) {
                          this.setState({ date: date });
                          this.loadEvents(
                            this.state.studio,
                            this.state.course._id,
                            date,
                            this.state.owner
                          );
                        }
                      }}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item>
                  <Typography>
                    {this.props.translate('manageRegistration.hostTimeZoneNotification')}:{' '}
                    {this.state.owner && moment.tz(this.state.owner.timezone).format('Z z')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Fragment>
        );
      }
    }
    return null;
  };

  renderAlternativeCourses = () => {
    if (this.state.loadingAlternatives) {
      return LayoutHelper.renderLoader();
    }
    if (
      this.state.alternativeCourses &&
      this.state.alternativeCourses.length > 0
    ) {
      return (
        <Grid
          container
          spacing={1}
          className={this.props.classes.alternativeCourses}
        >
          {this.state.alternativeCourses.map((alternativeCourse, index) => {
            return this.renderAlternativeCourse(alternativeCourse, index);
          })}
        </Grid>
      );
    }
    return null;
  };

  renderAlternativeCourse = (alternativeCourseData: AlternativeCourseData, index: number) => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    if (classDates) {
      return (
        <Grid item xs={12} key={index}>
          <Card>
            {teacher && (
              <CardHeader
                avatar={<Avatar src={teacher.avatar} />}
                title={teacher.name}
              ></CardHeader>
            )}
            {this.renderCourseRoom(course)}
            <Divider />
            <CardContent>
              {classDates.length === 0 ? (
                <Typography>
                  {this.props.translate(
                    'manageRegistration.noClassesAvailable'
                  )}
                </Typography>
              ) : (
                classDates.map((freeSlot, idx) => {
                  let classDateTime = moment.utc(freeSlot.classDateTime);
                  return (
                    <Chip
                      key={idx}
                      color="secondary"
                      variant="outlined"
                      label={classDateTime.format('HH:mm')}
                      onClick={() => {
                        this.goToAlternativeFreeSlot(
                          alternativeCourseData,
                          freeSlot
                        );
                      }}
                    />
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      );
    }
    return null;
  };

  goToAlternativeFreeSlot = (alternativeCourseData: AlternativeCourseData, freeSlot: FreeSlot) => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    if (this.state.studio && this.state.owner) {
      this.loadAltrnatives(
        this.state.studio,
        course,
        this.state.date,
        this.state.owner
      );
    }
    this.setState({
      course: course,
      teacher: teacher,
      selectedEvent: freeSlot,
      events: classDates,
      showAlternatives: false,
    });
  };

  renderRepeatableClass = (course?: Course) => {
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return this.renderCourseHeader(course);
      } else {
        return this.props.translate('manageRegistration.courseIsNotAvailable');
      }
    } else {
      return LayoutHelper.renderLoader();
    }
  };

  renderCourseHeader = (course: Course) => {
    if (!this.state.studio) return null;
    
    return (
      <Card>
        <CardHeader
          avatar={
            course.avatar ? (
              <Avatar
                src={course.avatar}
                className={this.props.classes.bigAvatar}
              />
            ) : (
              <Avatar
                src={this.state.studio.avatar}
                className={this.props.classes.avatar}
              ></Avatar>
            )
          }
          action={
            <IconButton
              onClick={() => {
                this.setState({
                  showPricingDetails: !this.state.showPricingDetails,
                });
              }}
            >
              <InfoIcon />
            </IconButton>
          }
          title={course.title}
          subheader={
            <Link href={routes.getStudioLink(this.state.studio)}>
              {' '}
              {this.state.studio.name}
            </Link>
          }
        ></CardHeader>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          <CardContent>{this.renderCourseDescription(course)}</CardContent>

          <CardContent>
            <div
              dangerouslySetInnerHTML={{ __html: this.state.studio.pricing || '' }}
              className="text"
            />
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  renderClasses = (course?: Course) => {
    if (!this.state.events) {
      return LayoutHelper.renderLoader();
    }
    if (this.state.events.length === 0 || !course) {
      return null;
    }
    const teacher = this.state.teacher;

    return (
      <Card>
        {teacher && (
          <CardHeader
            avatar={<Avatar src={teacher.avatar} />}
            title={teacher.name}
          ></CardHeader>
        )}
        {this.renderCourseRoom(course)}
        <Divider />
        <CardContent>
          <Grid container spacing={1}>
            {course.beforeBookingText && (
              <Grid item xs={12}>
                <Typography
                  dangerouslySetInnerHTML={{ __html: course.beforeBookingText }}
                ></Typography>
              </Grid>
            )}
            <Grid item>
              <Typography>
                {this.state.events.map((event, index) => {
                  let classDateTime = moment.utc(event.classDateTime);
                  return (
                    <Chip
                      key={index}
                      icon={this.state.selectedEvent === event ? <DoneIcon /> : undefined}
                      label={classDateTime.format('HH:mm')}
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        this.setState({ selectedEvent: event, showAlternatives: false, });
                      }}
                    />
                  );
                })}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  renderCourseRoom = (course: Course) => {
    const roomsMap = this.state.studio?.roomsMap;
    if (course.room.length === 1 && roomsMap) {
      const roomId = course.room[0];
      if (roomsMap[roomId]) {
        return (
          <CardContent>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <PinDrop />
              </Grid>
              <Grid item>{roomsMap[roomId].name}</Grid>
            </Grid>
          </CardContent>
        );
      }
    }
    return null;
  };

  renderCourseDescription = (course: Course) => {
    if (course.description) {
      return (
        <Typography component="div">
          <div dangerouslySetInnerHTML={{ __html: course.description }} />
        </Typography>
      );
    }
    return null;
  };
}

function mapStateToProps(state: any, ownProps: RouteComponentProps<RouteParams>) {
  let date = queryString.parse(ownProps.location.search).date as string | undefined;

  let dateMoment;
  if (!date) {
    dateMoment = moment.utc().add(1, 'days').startOf('day');
  } else {
    dateMoment = moment.utc(date, 'DD-MM-YYYY').startOf('day');
  }

  return {
    translate: getTranslate(state.locale),
    currentLanguage: getActiveLanguage(state.locale).code,
    user: state.user,
    studio: state.studio,
    domain: state.domain.domain,
    date: dateMoment,
  };
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(
          push(`${routes.class()}?keyword=${''}&location=${''}`)
        );
      } else {
        dispatch(push(`${routes.classId()}?keyword=${''}`));
      }
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new?: boolean }) => {
      dispatch(
        push(
          `${routes.clientAttendance({
            domainName: studio.domainName,
            groupId: groupId,
            id: attendanceId,
          })}${client.new ? '?new=true' : ''}`
        )
      );
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withStyles(styles)(GroupBookingCourseView)
);